import React, { useEffect, useState } from "react";
import { Button, Select, Space, Col, Row, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import styles from "./add-edit-section.module.css";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../../common/custom-button/custom-button";
import CustomTextfield from "../../../../../common/input/custom-textfield/custom-textfield";
import { fetchClass } from "../../../../../services/student";
import { createClassroom, updateClassroom } from "../../../../../services/class_routine";
import { COLOR_CODE } from "../../../../../constants/common";
import { fetchTeacherList } from "../../../../../services/teachers";
import CustomTypableSelect from "../../../../../common/input/custom-typable-select/custom-typable-select";

const { Option } = Select;

const AddEditSection = ({ open, onClose, editSectionData }) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      formName: "",
      sectionSets: [
        {
          sectionName: "",
          sectionColor: "",
          studentCapacity: "",
          teacher: ""
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "sectionSets",
  });
  const [loading, setLoading] = useState(false);
  const [classList, setClassList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);

  useEffect(() => {
    getClassList();
    getTeacherList();
  }, []);

  useEffect(() => {
    if (editSectionData?.classroom_id) {
      reset({
        formName: editSectionData?.level_name,
        sectionSets: [
          {
            sectionName: editSectionData?.classroom_name,
            sectionColor: editSectionData?.colour_code,
            studentCapacity: editSectionData?.capacity,
            teacher: editSectionData?.class_teacher_id
          },
        ],
      });
    } else if (editSectionData?.level_name) {
      reset({ formName: editSectionData?.level_name });
    }
  }, [editSectionData, reset]);

  const getClassList = async () => {
    try {
      setLoading(true);
      const response = await fetchClass();
      if (response?.status) {
        setClassList(response?.data?.data?.levels);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const getTeacherList = async () => {
    try {
      setLoading(true);
      const response = await fetchTeacherList();
      if (response?.status) {
        setTeacherList(response?.data?.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editSectionData?.classroom_id) {
        const payload = {
          level_name: data?.formName,
          name: data?.sectionSets?.[0]?.sectionName,
          colour_code: data?.sectionSets?.[0]?.sectionColor,
          class_teacher_id: data?.sectionSets?.[0]?.teacher,
          capacity: data?.sectionSets?.[0]?.studentCapacity
        };
        await updateClassroom(editSectionData?.classroom_id, payload);
      } else {
        const payload = data?.sectionSets?.map(item => ({
          level_name: data?.formName,
          name: item?.sectionName,
          colour_code: item?.sectionColor,
          class_teacher_id: item?.teacher,
          capacity: item?.studentCapacity
        }));
        await createClassroom(payload);
      }
      onClose();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <CustomDrawer
      title={`${editSectionData?.classroom_id ? 'Edit' : 'Add'} Section`}
      open={open}
      onClose={onClose}
      className={styles.drawer}
      width="50vw"
      drawerStyles={{ body: { padding: "24px" } }}
    >
   <form onSubmit={handleSubmit(onSubmit)}>
  <div className={styles.formSectionBox}>
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <div className={styles.add_form}>
        <div>
          <label
            style={{
              fontWeight: 500,
              marginBottom: 6,
              display: "inline-block",
            }}
          >
            Form Name<span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name="formName"
            control={control}
            rules={{ required: "Form name is required" }}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomTypableSelect
                labelRowor="#171717"
                placeholder="--Select--"
                showSearch
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                filterOption={(input, option) =>
                  option?.children
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                style={{ width: "210px" }}
                loading={loading}
                defaultOptions={classList?.map(item=> item?.name)}
              />
            )}
          />
        </div>

        {!editSectionData?.classroom_id ? (
          <CustomButton
            title="Add Section"
            type="text"
            icon={
              <div className={styles.iconCircle}>
                <PlusOutlined />
              </div>
            }
            className={styles.addSubjectBtn}
            showTooltip={false}
            onClick={() => append({ sectionName: "", sectionColor: "", studentCapacity: "", teacher: "" })}
          />
        ) : null}
      </div>

      {fields?.map((field, index) => (
        <Space 
          direction="vertical" 
          style={{ width: "100%" }} 
          size="large"
          key={field?.id}
        >
          {fields?.length > 1 && (
            <div>
              <Divider
                style={{
                  backgroundColor: "#d9d9d9",
                  marginTop: 8,
                }}
              />
              <div className={styles.delete}>
                <CustomButton
                  title="Delete"
                  type="text"
                  icon={<DeleteOutlined />}
                  className={styles.deleteSubjectBtn}
                  showTooltip={false}
                  onClick={() => remove(index)}
                />
              </div>
            </div>
          )}

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <label
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  display: "inline-block",
                }}
              >
                Section Name<span style={{ color: "red" }}>*</span>
              </label>
              <Controller
                name={`sectionSets.${index}.sectionName`}
                control={control}
                rules={{ required: "Section name is required" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextfield
                    labelColor="#171717"
                    showSearch
                    size="large"
                    value={value}
                    placeholder="Section Name"
                    onChange={onChange}
                    status={error ? "error" : ""}
                  />
                )}
              />
            </Col>

            <Col span={12}>
              <label
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  display: "inline-block",
                }}
              >
                Section Color<span style={{ color: "red" }}>*</span>
              </label>
              <Controller
                name={`sectionSets.${index}.sectionColor`}
                control={control}
                rules={{ required: "Section color is required" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelColor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                  >
                    {COLOR_CODE?.map((hex) => (
                      <Option key={hex} value={hex}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div
                            style={{
                              height: 20,
                              width: 20,
                              borderRadius: 2,
                              backgroundColor: `${hex}1A`,
                              border: `1px solid ${hex}66`,
                            }}
                          />
                          {hex}
                        </div>
                      </Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <label
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  display: "inline-block",
                }}
              >
                Class Teacher Name<span style={{ color: "red" }}>*</span>
              </label>
              <Controller
                name={`sectionSets.${index}.teacher`}
                control={control}
                rules={{ required: "Teacher select is required" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelColor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                  >
                    {teacherList?.map((option, index) => (
                      <Option key={`${option?.id}-${index}`} value={option?.id}>
                        {option?.full_name}
                      </Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>

            <Col span={12}>
              <label
                style={{
                  fontWeight: 500,
                  marginBottom: 6,
                  display: "inline-block",
                }}
              >
                Student Capacity<span style={{ color: "red" }}>*</span>
              </label>
              <Controller
                name={`sectionSets.${index}.studentCapacity`}
                control={control}
                rules={{ required: "Capacity is required" }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextfield
                    labelColor="#171717"
                    showSearch
                    size="large"
                    type="number"
                    value={value}
                    placeholder="Student Capacity"
                    onChange={onChange}
                    status={error ? "error" : ""}
                  />
                )}
              />
            </Col>
          </Row>
        </Space>
      ))}
    </Space>
  </div>

  <div className={styles.formActions}>
    <Space
      style={{
        width: "100%",
        justifyContent: "space-between",
      }}
    >
      <Button onClick={onClose}>Cancel</Button>
      <Button
        type="primary"
        htmlType="submit"
        style={{
          background: "rgba(57, 181, 74, 1)",
          borderColor: "rgba(57, 181, 74, 1)",
        }}
      >
        Save
      </Button>
    </Space>
  </div>
</form>

    </CustomDrawer>
  );
};

export default AddEditSection;